import { useEffect, useState } from "react";
import { Drawer } from "antd";
import { FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { FilterPopoverProps } from "./types";
import BaseButton from "../Button/BaseButton";
import BasePopover from "../Popover/BasePopover";
import { useResponsive } from "@/shared/hooks/useBreakpoints";

import "./styles.css";

const FilterPopover = ({
  fields,
  activeFilterCount,
  title,
  handleClear,
  handleSearch,
}: FilterPopoverProps) => {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const { isMobile } = useResponsive();

  useEffect(() => {
    if (!isOpen || isMobile) {
      return undefined;
    }
    const opener =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (event.key !== "Escape" || target?.closest(".ant-select-open")) {
        return;
      }
      setIsOpen(false);
      opener?.focus();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isMobile]);

  const handleFiltersClear = () => {
    handleClear();
    setIsOpen(false);
  };

  const handleFilterSearch = () => {
    handleSearch();
    setIsOpen(false);
  };

  const popoverContent = (
    <div className="flex flex-col gap-400">
      <div className="flex flex-col gap-250">
        {fields.map((field, index) => (
          <div
            key={`${field.label}-${index}`}
            className="flex flex-col gap-100"
          >
            <p className="body-s-medium">{field.label}</p>
            {field.component}
          </div>
        ))}
      </div>

      <div className="flex justify-between items-center">
        <BaseButton
          variant="tertiary"
          size="small"
          onClick={handleFiltersClear}
          disabled={activeFilterCount === 0}
        >
          {t("buttons.clearFilters")}
        </BaseButton>

        <BaseButton
          variant="primary"
          size="small"
          leadingIcon={<SearchOutlined />}
          onClick={handleFilterSearch}
        >
          {t("buttons.search")}
        </BaseButton>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div>
        <Drawer
          open={isOpen}
          onClose={() => setIsOpen(false)}
          placement="bottom"
          title={title}
        >
          {popoverContent}
        </Drawer>

        <BaseButton
          variant="tertiary"
          size="small"
          leadingIcon={<FilterOutlined />}
          className="filter-popover-button flex items-center justify-center gap-200"
          onClick={() => setIsOpen(true)}
        >
          {t("buttons.filters")}

          {activeFilterCount && activeFilterCount > 0 ? (
            <span>{activeFilterCount}</span>
          ) : null}
        </BaseButton>
      </div>
    );
  }

  return (
    <BasePopover
      content={popoverContent}
      title={title}
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomRight"
      classNames={{ root: "filters-popover" }}
    >
      <BaseButton
        variant="tertiary"
        size="small"
        leadingIcon={<FilterOutlined />}
        className="filter-popover-button flex items-center justify-center gap-200"
      >
        {t("buttons.filters")}

        {activeFilterCount && activeFilterCount > 0 ? (
          <span>{activeFilterCount}</span>
        ) : null}
      </BaseButton>
    </BasePopover>
  );
};

export default FilterPopover;
